import {
  Alert,
  AmenitiesElectricityGeneratorIcon,
  AmenitiesFridgeFridgeIcon,
  AmenitiesOutdoorFirepitIcon,
  AmenitiesOutdoorGrillIcon,
  AmenitiesOutdoorPicnicTableIcon,
  AmenitiesQuietAreaIcon,
  AmenitiesShadeIcon,
  AmenitiesWaterfuelSewageTankIcon,
  AmenitiesWaterfuelWatertankIcon,
  EAlertVariant,
  EHeadingStyleVariant,
  ETextStyleVariant,
  GeneralListIcon,
  Heading,
  Text,
} from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import React, { SVGProps } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Breadcrumbs from '@/components/common/Breadcrumbs';
import { IAddress, IAttr, IAttribute } from '@/components/route/campground/campgroundTypes';
import { IBreadcrumb } from '@/utility/commonDataTypes';

import { Description } from './Description';

type TAboutProps = {
  title: string;
  description: string;
  attributes?: IAttribute;
  breadcrumbs: IBreadcrumb[];
  address?: IAddress;
};

export const About = ({ title, description, attributes, breadcrumbs, address }: TAboutProps) => {
  const amenities = getAmenities(attributes);
  const intl = useIntl();
  const campgroundAddress =
    address &&
    intl.formatMessage(
      { defaultMessage: 'Campground in {location}', id: 'rltDTf' },
      { location: `${address.city}, ${address.state}` },
    );

  return (
    <>
      <div className="hidden md:block">
        <Breadcrumbs breadcrumbs={breadcrumbs.slice(0, 3)} />
      </div>

      <div
        className={clsx('mt-6 grid gap-6 md:gap-12 lg:gap-24', {
          'md:grid-cols-2': amenities.length,
        })}>
        <div>
          <Heading variant={EHeadingStyleVariant.H4}>{title}</Heading>

          {address && (
            <Text variant={ETextStyleVariant.MediumRegular} className="my-4">
              {campgroundAddress}
            </Text>
          )}

          <div className="mt-6 mb-4 md:mt-8">
            <Description description={description} />
          </div>
        </div>

        <div>
          {!!amenities.length && (
            <div>
              <Text variant={ETextStyleVariant.LargeBold}>
                <FormattedMessage defaultMessage="Amenities" id="e38GOF" />
              </Text>

              <div className="mt-6 grid gap-y-2 gap-x-4 lg:grid-cols-2">
                {amenities.map(({ name, Icon }, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Icon width={24} height={24} />
                    <Text variant={ETextStyleVariant.MediumRegular}>{name}</Text>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Alert
            variant={EAlertVariant.Info}
            title={intl.formatMessage({
              defaultMessage: 'Booking for this campground is not enabled yet',
              id: 'jN3Pbx',
            })}
            content={intl.formatMessage({
              defaultMessage:
                'For information regarding reservations and more information, contact this campground directly.',
              id: '4Y70wb',
            })}
            className="mt-12"
          />
        </div>
      </div>
    </>
  );
};

const amenitiesIconsMap: Record<string, (props: SVGProps<SVGSVGElement>) => JSX.Element> = {
  BBQ: AmenitiesOutdoorGrillIcon,
  'Fire pit': AmenitiesOutdoorFirepitIcon,
  'Food locker': AmenitiesFridgeFridgeIcon,
  'Grills/Fire ring': AmenitiesOutdoorGrillIcon,
  'Picnic table': AmenitiesOutdoorPicnicTableIcon,
  'Picnic tables': AmenitiesOutdoorPicnicTableIcon,
  'Electricity hookup': AmenitiesElectricityGeneratorIcon,
  'Sewer hookup': AmenitiesWaterfuelSewageTankIcon,
  'Water hookup': AmenitiesWaterfuelWatertankIcon,
  Shade: AmenitiesShadeIcon,
  'Quiet area': AmenitiesQuietAreaIcon,
};

const getAmenities = (attributes?: IAttribute) => {
  const { attr: _, ...amenities } = attributes || {};
  return Object.values(amenities).reduce<
    Array<{ name: string; Icon: (props: SVGProps<SVGSVGElement>) => JSX.Element }>
  >(
    (acc, a) => [
      ...acc,
      ...a.map(({ name }: IAttr) => ({
        name,
        Icon: amenitiesIconsMap[name] || GeneralListIcon,
      })),
    ],
    [],
  );
};
